import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, User, CreditCard, Tag, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import ExitIntentModal from '../components/ExitIntentModal';

const LOGO_URL = "/logo.svg";

const steps = [
    { id: 1, label: 'Business', icon: Building2 },
    { id: 2, label: 'Account', icon: User },
    { id: 3, label: 'Payment', icon: CreditCard },
];

export default function Register() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const [business, setBusiness] = useState({ business_name: '', business_address: '', business_phone: '', business_email: '' });
    const [manager, setManager] = useState({ manager_name: '', manager_email: '', manager_password: '', confirm_password: '' });
    const [couponCode, setCouponCode] = useState('');
    const [couponResult, setCouponResult] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);

    const validateCoupon = async () => {
        if (!couponCode.trim()) return;
        setValidatingCoupon(true);
        try {
            const { data } = await registerApi.validateCoupon(couponCode);
            setCouponResult(data);
            if (data.valid) toast.success('Coupon applied!');
            else toast.error(data.message);
        } catch {
            setCouponResult({ valid: false, message: 'Failed to validate coupon' });
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handleSubmit = async () => {
        if (manager.manager_password !== manager.confirm_password) {
            toast.error('Passwords do not match');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                business: { business_name: business.business_name, business_address: business.business_address, business_phone: business.business_phone, business_email: business.business_email || null },
                manager: { manager_name: manager.manager_name, manager_email: manager.manager_email, manager_password: manager.manager_password },
                coupon_code: couponResult?.valid ? couponCode : null,
            };
            const { data } = await registerApi.register(payload);

            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.success(data.message);
                navigate('/login');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-lg">
                <div className="text-center mb-6">
                    <img src={LOGO_URL} alt="Food Safe Hub" className="h-14 mx-auto mb-3" />
                    <h1 className="text-2xl font-bold text-slate-100">Register Your Business</h1>
                    <p className="text-slate-400 text-sm mt-1">Set up SFBB compliance for your food business</p>
                </div>

                {/* Step indicator */}
                <div className="flex items-center justify-center gap-2 mb-6">
                    {steps.map((s, i) => (
                        <React.Fragment key={s.id}>
                            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${step >= s.id ? 'brand-bg-subtle brand-text' : 'bg-slate-800 text-slate-500'}`}>
                                <s.icon className="h-3.5 w-3.5" />
                                <span>{s.label}</span>
                            </div>
                            {i < steps.length - 1 && <div className={`w-8 h-px ${step > s.id ? 'bg-[var(--brand-primary)]' : 'bg-slate-700'}`} />}
                        </React.Fragment>
                    ))}
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                    {/* Step 1: Business Details */}
                    {step === 1 && (
                        <div className="space-y-4" data-testid="register-step-business">
                            <h2 className="text-lg font-semibold text-slate-200 mb-3">Business Details</h2>
                            <div>
                                <Label className="text-slate-300">Business Name *</Label>
                                <Input data-testid="register-business-name" value={business.business_name}
                                    onChange={(e) => setBusiness({ ...business, business_name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. The Coffee Shop" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Address</Label>
                                <Input value={business.business_address} onChange={(e) => setBusiness({ ...business, business_address: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Business address" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-300">Phone</Label>
                                    <Input value={business.business_phone} onChange={(e) => setBusiness({ ...business, business_phone: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Phone number" />
                                </div>
                                <div>
                                    <Label className="text-slate-300">Business Email</Label>
                                    <Input type="email" value={business.business_email} onChange={(e) => setBusiness({ ...business, business_email: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="info@business.com" />
                                </div>
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button onClick={() => setStep(2)} disabled={!business.business_name.trim()} data-testid="register-next-step-1"
                                    className="brand-btn font-medium">
                                    Next <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Manager Account */}
                    {step === 2 && (
                        <div className="space-y-4" data-testid="register-step-account">
                            <h2 className="text-lg font-semibold text-slate-200 mb-3">Manager Account</h2>
                            <div>
                                <Label className="text-slate-300">Your Name *</Label>
                                <Input data-testid="register-manager-name" value={manager.manager_name}
                                    onChange={(e) => setManager({ ...manager, manager_name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Full name" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Email *</Label>
                                <Input data-testid="register-manager-email" type="email" value={manager.manager_email}
                                    onChange={(e) => setManager({ ...manager, manager_email: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="you@business.com" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Password *</Label>
                                <Input data-testid="register-manager-password" type="password" value={manager.manager_password}
                                    onChange={(e) => setManager({ ...manager, manager_password: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Min 6 characters" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Confirm Password *</Label>
                                <Input type="password" value={manager.confirm_password}
                                    onChange={(e) => setManager({ ...manager, confirm_password: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Confirm password" />
                            </div>
                            <div className="flex justify-between pt-2">
                                <Button variant="outline" onClick={() => setStep(1)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                                </Button>
                                <Button onClick={() => setStep(3)} data-testid="register-next-step-2"
                                    disabled={!manager.manager_name || !manager.manager_email || !manager.manager_password}
                                    className="brand-btn font-medium">
                                    Next <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Payment / Coupon */}
                    {step === 3 && (
                        <div className="space-y-4" data-testid="register-step-payment">
                            <h2 className="text-lg font-semibold text-slate-200 mb-3">Subscription</h2>
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-300">Monthly plan</span>
                                    <span className="text-xl font-bold brand-text">
                                        {couponResult?.valid && couponResult.coupon_type === 'free_trial' ? 'FREE TRIAL' : '£29.99/mo'}
                                    </span>
                                </div>
                                {couponResult?.valid && couponResult.coupon_type !== 'free_trial' && (
                                    <p className="text-sm text-emerald-400 mt-1">{couponResult.discount_description}</p>
                                )}
                            </div>
                            <div>
                                <Label className="text-slate-300">Coupon Code (optional)</Label>
                                <div className="flex gap-2 mt-1">
                                    <Input data-testid="register-coupon-input" value={couponCode}
                                        onChange={(e) => { setCouponCode(e.target.value); setCouponResult(null); }}
                                        className="bg-slate-800 border-slate-700 text-slate-100" placeholder="Enter code" />
                                    <Button type="button" variant="outline" onClick={validateCoupon} disabled={validatingCoupon || !couponCode.trim()}
                                        className="border-slate-700 text-slate-300 hover:bg-slate-800 shrink-0" data-testid="register-validate-coupon">
                                        <Tag className="h-4 w-4 mr-1" /> {validatingCoupon ? '...' : 'Apply'}
                                    </Button>
                                </div>
                                {couponResult && (
                                    <p className={`text-xs mt-1.5 ${couponResult.valid ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {couponResult.valid ? (
                                            <span className="flex items-center gap-1"><Check className="h-3 w-3" /> {couponResult.discount_description}{couponResult.description && ` - ${couponResult.description}`}</span>
                                        ) : couponResult.message}
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-between pt-2">
                                <Button variant="outline" onClick={() => setStep(2)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                                </Button>
                                <Button onClick={handleSubmit} disabled={loading} data-testid="register-submit-button"
                                    className="brand-btn font-semibold">
                                    {loading ? 'Processing...' : couponResult?.valid && couponResult.coupon_type === 'free_trial' ? 'Start Free Trial' : 'Proceed to Payment'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <p className="text-center mt-4 text-sm text-slate-400">
                    Already have an account?{' '}
                    <Link to="/login" className="brand-text hover:opacity-80 font-medium" data-testid="login-link">Sign in</Link>
                </p>
            </div>
            <ExitIntentModal />
        </div>
    );
}
